import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { moodboardService } from '../lib/moodboardService';
import { aiTouchupService } from '../lib/aiTouchupService';
import { userGalleryService } from '../lib/userGalleryService';
import { upstagingService } from '../lib/restyleService';
import { furnitureRemovalService } from '../lib/furnitureRemovalService';
import { UpstagingGeneration } from '../types/restyle';

import { ImageLightbox, LightboxMetadata } from './ImageLightbox';

interface UnifiedGeneration {
  id: string;
  type: 'moodboard' | 'change' | 'remove' | 'upload' | 'upstaging' | 'furniture';
  imageUrl: string;
  images?: Array<{
    id: string;
    image_url: string;
    image_name?: string;
    is_primary: boolean;
    sort_order: number;
  }>;
  createdAt: string;
  prompt?: string;
  status?: string;
  processingId?: string;
  tokensSpent?: number;
  // Additional moodboard fields
  roomType?: string;
  roomStyle?: string;
  roomSize?: string;
  category?: string;
  // Upload fields
  originalFilename?: string;
  fileSize?: number;
  compressionRatio?: number;
  // Upstaging fields
  style?: string;
}

const getBadgeStyles = (type: string) => {
  switch (type) {
    case 'moodboard':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'change':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'remove':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'upload':
      return 'bg-purple-100 text-purple-800 border-purple-300';
    case 'furniture':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'upstaging':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const getBadgeIcon = (type: string) => {
  switch (type) {
    case 'moodboard':
      return '🎨';
    case 'change':
      return '✏️';
    case 'remove':
      return '🗑️';
    case 'upload':
      return '📤';
    case 'furniture':
      return '🪑';
    case 'upstaging':
      return '🏠';
    default:
      return '🔧';
  }
};

const getBadgeText = (type: string) => {
  switch (type) {
    case 'moodboard':
      return 'Moodboard';
    case 'change':
      return 'AI Change';
    case 'remove':
      return 'AI Remove';
    case 'upload':
      return 'My Upload';
    case 'furniture':
      return 'Furniture Removal';
    case 'upstaging':
      return 'Restyle';
    default:
      return 'Unknown';
  }
};

export const AllGenerationsViewer: React.FC = () => {
  const { user } = useAuth();
  const [generations, setGenerations] = useState<UnifiedGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState<UnifiedGeneration | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'moodboard' | 'change' | 'remove' | 'upload' | 'upstaging' | 'furniture'>('all');

  useEffect(() => {
    const fetchAllGenerations = async () => {
      if (!user?.id) {
        setGenerations([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if multiple images support is enabled
        const supportCheck = await moodboardService.checkMultipleImagesSupport();
        console.log('Multiple images support check:', supportCheck);

        // Fetch moodboard generations, AI touchup operations, user uploads, upstaging generations, and furniture removal operations in parallel
        const [moodboardGenerations, aiTouchupOperations, userUploads, upstagingGenerations, furnitureRemovalOperations] = await Promise.all([
          moodboardService.getMoodboardGenerationsWithImages(user.id),
          aiTouchupService.getUserOperations(user.id),
          userGalleryService.getUserUploads(user.id),
          upstagingService.getGenerations(user.id),
          furnitureRemovalService.getUserOperations(user.id)
        ]);

        const unifiedGenerations: UnifiedGeneration[] = [];

        // Add moodboard generations
        moodboardGenerations
          .filter(gen => gen.result_image_url && gen.status === 'completed')
          .forEach(gen => {
            unifiedGenerations.push({
              id: gen.id,
              type: 'moodboard',
              imageUrl: gen.result_image_url!,
              images: gen.images,
              createdAt: gen.created_at,
              status: gen.status,
              processingId: gen.processing_id,
              // Note: We'd need to fetch additional room details from related tables
              // For now, these will be undefined but the structure is ready
            });
          });

        // Add AI touchup operations
        aiTouchupOperations
          .filter(op => op.result_image_url && op.processing_status === 'completed')
          .forEach(op => {
            unifiedGenerations.push({
              id: op.id,
              type: op.operation_type === 'change' ? 'change' : 'remove',
              imageUrl: op.result_image_url!,
              createdAt: op.created_at,
              prompt: op.prompt,
              status: op.processing_status,
              processingId: op.processing_id,
              tokensSpent: op.tokens_spent
            });
          });

        // Add user uploads
        userUploads.forEach(upload => {
          unifiedGenerations.push({
            id: upload.id,
            type: 'upload',
            imageUrl: upload.fileUrl,
            createdAt: upload.createdAt,
            originalFilename: upload.originalFilename,
            fileSize: upload.fileSize,
            compressionRatio: upload.compressionRatio
          });
        });

        // Add upstaging generations
        upstagingGenerations
          .filter((gen: UpstagingGeneration) => gen.upstaged_image_url)
          .forEach((gen: UpstagingGeneration) => {
            unifiedGenerations.push({
              id: gen.id,
              type: 'upstaging',
              imageUrl: gen.upstaged_image_url,
              createdAt: gen.created_at,
              prompt: gen.prompt,
              style: gen.style,
              roomType: gen.room_type
            });
          });

        // Add furniture removal operations
        furnitureRemovalOperations
          .filter(op => op.result_image_url && op.processing_status === 'completed')
          .forEach(op => {
            unifiedGenerations.push({
              id: op.id,
              type: 'furniture',
              imageUrl: op.result_image_url!,
              createdAt: op.created_at,
              prompt: op.operation_type === 'clean_selected' ? op.clean_selected_prompt : 'Clear all furniture',
              status: op.processing_status,
              processingId: op.processing_id,
              tokensSpent: op.tokens_spent
            });
          });

        // Sort by creation date (newest first)
        unifiedGenerations.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setGenerations(unifiedGenerations);
      } catch (err) {
        console.error('Failed to fetch generations:', err);
        setError('Failed to load your generations');
      } finally {
        setLoading(false);
      }
    };

    fetchAllGenerations();
  }, [user?.id]);

  const openLightbox = (generation: UnifiedGeneration) => {
    setSelectedGeneration(generation);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedGeneration(null);
  };

  const getLightboxMetadata = (generation: UnifiedGeneration): LightboxMetadata => {
    return {
      id: generation.id,
      type: generation.type,
      createdAt: generation.createdAt,
      prompt: generation.prompt,
      status: generation.status,
      processingId: generation.processingId,
      tokensSpent: generation.tokensSpent,
      roomType: generation.roomType,
      roomStyle: generation.roomStyle,
      roomSize: generation.roomSize,
      category: generation.category,
      images: generation.images,
      originalFilename: generation.originalFilename,
      fileSize: generation.fileSize,
      compressionRatio: generation.compressionRatio,
      style: generation.style,
    };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Your Generations</h3>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Your Generations</h3>
        <div className="text-center text-red-600 py-8">{error}</div>
      </div>
    );
  }

  // Filter generations based on selected type
  const filteredGenerations = generations.filter(generation => {
    return filterType === 'all' || generation.type === filterType;
  });

  if (generations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Your Generations</h3>
        <div className="text-center text-gray-500 py-8">
          <p className="mb-2">🎨 No generations yet!</p>
          <p className="text-sm">Create your first moodboard or use AI touchup to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Your Generations</h3>
        <span className="text-sm text-gray-500">
          {filteredGenerations.length} of {generations.length} generation{generations.length !== 1 ? 's' : ''}
        </span>
      </div>
      
      {/* Filter buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { key: 'all', label: 'All', icon: '🎯' },
          { key: 'upload', label: 'My Uploads', icon: '📤' },
          { key: 'moodboard', label: 'Moodboard', icon: '🎨' },
          { key: 'change', label: 'AI Change', icon: '✏️' },
          { key: 'remove', label: 'AI Remove', icon: '🗑️' },
          { key: 'furniture', label: 'Furniture Removal', icon: '🪑' },
          { key: 'upstaging', label: 'Restyle', icon: '🏠' }
        ].map((filter) => (
          <button
            key={filter.key}
            onClick={() => setFilterType(filter.key as typeof filterType)}
            className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium transition-colors ${
              filterType === filter.key
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
            }`}
          >
            <span className="mr-1">{filter.icon}</span>
            {filter.label}
            {filter.key !== 'all' && (
              <span className="ml-1 text-xs opacity-75">
                ({generations.filter(g => g.type === filter.key).length})
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Filtered results */}
      {filteredGenerations.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <p className="mb-2">📭 No {filterType === 'all' ? '' : getBadgeText(filterType).toLowerCase()} generations found</p>
          <p className="text-sm">
            {filterType === 'all' 
              ? 'Create your first generation to get started.' 
              : `Try selecting a different filter or create some ${getBadgeText(filterType).toLowerCase()} generations.`
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredGenerations.map((generation) => (
          <div key={generation.id} className="relative group">
            <div 
              className="aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer border border-gray-200 hover:border-gray-300 transition-colors"
              onClick={() => openLightbox(generation)}
            >
              <img
                src={generation.imageUrl}
                alt={`${getBadgeText(generation.type)} Generation`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23f3f4f6"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%236b7280" font-size="12">Image not found</text></svg>';
                }}
              />
            </div>
            
            {/* Badge */}
            <div className="absolute top-2 left-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium shadow-sm ${getBadgeStyles(generation.type)}`}>
                <span className="mr-1">{getBadgeIcon(generation.type)}</span>
                {getBadgeText(generation.type)}
              </span>
            </div>

            {/* Multiple images indicator */}
            {generation.images && generation.images.length > 1 && (
              <div className="absolute top-2 right-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 shadow-sm">
                  📸 {generation.images.length}
                </span>
              </div>
            )}

            {/* Token cost indicator */}
            {generation.tokensSpent && (
              <div className={`absolute top-2 ${generation.images && generation.images.length > 1 ? 'right-16' : 'right-2'}`}>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 shadow-sm">
                  🪙 {generation.tokensSpent}
                </span>
              </div>
            )}

            {/* Info tooltip */}
            {(generation.prompt || generation.originalFilename) && (
              <div className="absolute bottom-2 left-2 right-2">
                <div className="bg-black bg-opacity-80 text-white text-xs p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                  {generation.prompt && (
                    <p className="truncate" title={generation.prompt}>
                      {generation.prompt}
                    </p>
                  )}
                  {generation.originalFilename && (
                    <div>
                      <p className="truncate" title={generation.originalFilename}>
                        📁 {generation.originalFilename}
                      </p>
                      {generation.fileSize && (
                        <p className="text-gray-300">
                          📊 {(generation.fileSize / 1024 / 1024).toFixed(2)}MB
                          {generation.compressionRatio && ` (${generation.compressionRatio.toFixed(1)}% smaller)`}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Date */}
            <div className="mt-2 text-xs text-gray-500 text-center">
              {new Date(generation.createdAt).toLocaleDateString()}
            </div>
          </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {selectedGeneration && (
        <ImageLightbox
          isOpen={lightboxOpen}
          imageUrl={selectedGeneration.imageUrl}
          alt={`${getBadgeText(selectedGeneration.type)} Generation`}
          metadata={getLightboxMetadata(selectedGeneration)}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
}; 